import { useLocation, useNavigate }             from "react-router-dom";
import { useState,    useEffect,  useContext }  from "react";
import useUser, { getAuthorized }               from "../../../hooks/User";
import { ROUTES } from "../../../routes";

import ConfirmChangesContextProvider, { ConfirmChangesContext }  from "../../../contexts/ConfirmChangesContext";

import VerticalSplashImage from "../../../components/SplashImage";
import SEO from "../../../components/SEO";

import "./confirmchange.css";

// Icon components
const EmailIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
    </svg>
);

const DoctorIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 21h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2z"></path>
        <path d="M12 7v6"></path>
        <path d="M9 10h6"></path>
        <circle cx="12" cy="17" r="1.5"></circle>
    </svg>
);

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);

function ConfirmCard({ title, description, icon: Icon, state, setState, onSubmit, isLoading, error, pattern, placeholder, label }) {
    const [localError, setLocalError] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!state || state.trim() === "") {
            setLocalError("Введите код подтверждения");
            return;
        }
        
        if (pattern && !pattern.test(state)) {
            setLocalError("Неверный формат кода");
            return;
        }
        
        setLocalError("");
        onSubmit();
    };

    return (
        <div className="confirm-card">
            <div className="confirm-card-icon">
                <Icon />
            </div>
            <h2 className="confirm-card-title">{title}</h2>
            <p className="confirm-card-description">{description}</p>
            
            <form onSubmit={handleSubmit} className="confirm-form">
                <div className="confirm-input-wrapper">
                    <input
                        type="text"
                        value={state}
                        onChange={(e) => {
                            setState(e.target.value);
                            setLocalError("");
                        }}
                        placeholder={placeholder}
                        className={`confirm-input ${(error || localError) ? "confirm-input-error" : ""}`}
                        autoFocus
                        maxLength={10}
                    />
                    {(error || localError) && (
                        <span className="confirm-error">{error || localError}</span>
                    )}
                </div>
                
                <button 
                    type="submit" 
                    className="confirm-btn"
                    disabled={isLoading}>
                    {isLoading ? (
                        <span className="confirm-loading">
                            <span className="spinner"></span>
                            Проверка...
                        </span>
                    ) : (
                        <>
                            <CheckIcon />
                            Подтвердить
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}

function ConfirmHandler() {
    const location    = useLocation();
    const nav         = useNavigate();

    const [firstTime,  setFirstTime]  = useState(true);

    const [emailCode,  setEmailCode]  = useState("");
    const [doctorCode, setDoctorCode] = useState("");

    const [emailLoading, setEmailLoading] = useState(false);
    const [doctorLoading, setDoctorLoading] = useState(false);
    
    const [emailError, setEmailError] = useState("");
    const [doctorError, setDoctorError] = useState("");

    const { confirmEmailChange, setConfirmEmail, confirmRoleChange, setConfirmRole } = useContext(ConfirmChangesContext);

    const {error} = useUser();

    useEffect(() => {
        if (error) nav(ROUTES.AUTH, {viewTransition: true});
    }, [error, nav]);

    useEffect(() => {
        if (!firstTime) return;

        const coderequired = location.state?.coderequired;

        if (!coderequired || coderequired.length === 0) {
            nav(ROUTES.PROFILE, {viewTransition: true});
            return;
        }

        // Set all as confirmed initially, then uncheck required ones
        setConfirmEmail(true);
        setConfirmRole(true);

        for (let confirming of coderequired) {
            if (confirming === "email")    setConfirmEmail(false);
            if (confirming === "isdoctor") setConfirmRole(false);
        }

        setFirstTime(false);
    }, [location, nav, setConfirmEmail, setConfirmRole, firstTime]);

    useEffect(() => {
        if (!firstTime && confirmEmailChange && confirmRoleChange) {
            nav(ROUTES.PROFILE, {viewTransition: true});
        }
    }, [firstTime, confirmEmailChange, confirmRoleChange, nav]);

    const handleEmailConfirm = async () => {
        setEmailLoading(true);
        setEmailError("");
        
        try {
            const response = await getAuthorized("/client/reset_email", {
                params: { verification_code_value: emailCode }
            });
            
            if (response.status >= 200 && response.status < 300) {
                setConfirmEmail(true);
                window.dispatchEvent(new CustomEvent('user:login'));
            }
        } catch (err) {
            setEmailError("Неверный код подтверждения");
        } finally {
            setEmailLoading(false);
        }
    };

    const handleDoctorConfirm = async () => {
        setDoctorLoading(true);
        setDoctorError("");
        
        try {
            const response = await getAuthorized("/client/enable_doctor_features", {
                params: { access_key: doctorCode }
            });
            
            if (response.status >= 200 && response.status < 300) {
                setConfirmRole(true);
            }
        } catch (err) {
            setDoctorError("Неверный код доступа");
        } finally {
            setDoctorLoading(false);
        }
    };

    const progressTotal = (confirmEmailChange ? 1 : 0) + (confirmRoleChange ? 1 : 0);
    const progressMax = (!firstTime ? (location.state?.coderequired?.length || 0) : 0);

    return (
        <>
        <SEO title="Подтверждение изменений" description="Подтвердите изменение email или статуса врача" noindex={true} />
        <div className="confirm-page">
            {/* Progress indicator */}
            {!firstTime && progressMax > 1 && (
                <div className="confirm-progress">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ width: `${(progressTotal / progressMax) * 100}%` }}>
                        </div>
                    </div>
                    <span className="progress-text">
                        Подтверждено {progressTotal} из {progressMax}
                    </span>
                </div>
            )}

            <div className="confirm-cards">
                {!confirmEmailChange && (
                    <ConfirmCard
                        title="Подтвердите смену email"
                        description="Мы отправили код подтверждения на вашу новую электронную почту. Введите его ниже для завершения изменения."
                        icon={EmailIcon}
                        state={emailCode}
                        setState={setEmailCode}
                        onSubmit={handleEmailConfirm}
                        isLoading={emailLoading}
                        error={emailError}
                        pattern={/^\w{6}$/}
                        placeholder="Введите 6-значный код"
                        label="Код подтверждения"
                    />
                )}

                {!confirmRoleChange && (
                    <ConfirmCard
                        title="Подтвердите статус врача"
                        description="Введите код доступа, выданный администратором системы, для активации функций врача."
                        icon={DoctorIcon}
                        state={doctorCode}
                        setState={setDoctorCode}
                        onSubmit={handleDoctorConfirm}
                        isLoading={doctorLoading}
                        error={doctorError}
                        placeholder="Введите код доступа"
                        label="Код доступа"
                    />
                )}
            </div>

            {/* Skip/Back option */}
            <div className="confirm-footer">
                <button 
                    className="confirm-back-btn"
                    onClick={() => nav(ROUTES.PROFILE, {viewTransition: true})}>
                    Вернуться в профиль
                </button>
            </div>
        </div>
        </>
    );
}

export default function ConfirmChange() {
    return (
        <VerticalSplashImage>
            <ConfirmChangesContextProvider>
                <ConfirmHandler/>
            </ConfirmChangesContextProvider>
        </VerticalSplashImage>
    );
}
